import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bookmark, Plus, Trash2, Lock, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Collections() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isPublic, setIsPublic] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) navigate("/login");
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/collections/mine")
                .then((r) => setCollections(r.data?.items || []))
                .finally(() => setLoading(false));
        }
    }, [user]);

    async function create() {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post("/collections", {
                name: name.trim(), description: description.trim(), is_public: isPublic,
            });
            setCollections((c) => [data, ...c]);
            setName(""); setDescription(""); setIsPublic(true);
            toast.success("Collection created");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Create failed");
        } finally {
            setCreating(false);
        }
    }

    async function remove(id) {
        if (!window.confirm("Delete this collection?")) return;
        try {
            await api.delete(`/collections/${id}`);
            setCollections((c) => c.filter((x) => x.id !== id));
            toast.success("Deleted");
        } catch (e) { toast.error("Delete failed"); }
    }

    if (!user) return null;

    return (
        <div className="ok-container py-10 max-w-4xl" data-testid="collections-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Research Tools · UNVEILED</div>
            <h1 className="font-display text-3xl">Research Collections</h1>
            <p className="opacity-70 mt-2 text-sm leading-relaxed max-w-2xl">
                Curate sets of encounters, species, and media into named research briefs. Public collections appear on your
                witness profile so other investigators can follow your work.
            </p>

            <div className="ok-card p-5 mt-6" data-testid="collections-create">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-3">New collection</div>
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name, e.g. ‘Skinwalker Ranch primary sources’"
                    className="ok-input w-full text-sm"
                    data-testid="collection-name-input"
                />
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    placeholder="Short description (optional)"
                    className="ok-input w-full text-sm mt-2"
                    data-testid="collection-desc-input"
                />
                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                    <label className="text-xs opacity-80 inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="collection-public-input" />
                        Public · visible on my profile
                    </label>
                    <button onClick={create} disabled={creating || !name.trim()} className="ok-btn-primary text-sm inline-flex items-center gap-1" data-testid="collection-create-btn">
                        {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create
                    </button>
                </div>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3">Your collections ({collections.length})</div>

            {loading ? (
                <div className="text-xs font-mono opacity-50">Loading…</div>
            ) : collections.length === 0 ? (
                <div className="ok-card p-6 text-sm opacity-70 text-center">No collections yet.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="collections-grid">
                    {collections.map((c) => (
                        <Link key={c.id} to={`/collections/${c.id}`} className="ok-card p-5 block hover:border-[var(--ok-accent)] transition" data-testid={`collection-card-${c.id}`}>
                            <div className="flex items-start justify-between">
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-1">
                                    <Bookmark className="w-3 h-3" />
                                    {c.items?.length || 0} items
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-1">
                                    {c.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                    {c.is_public ? "Public" : "Private"}
                                </div>
                            </div>
                            <div className="font-display text-xl mt-2">{c.name}</div>
                            {c.description && <p className="text-xs opacity-70 mt-1 line-clamp-2">{c.description}</p>}
                            <div className="mt-3 flex items-center justify-between">
                                <span className="font-mono text-[10px] opacity-50">Updated {new Date(c.updated_at).toLocaleDateString()}</span>
                                <button
                                    onClick={(e) => { e.preventDefault(); remove(c.id); }}
                                    className="text-[10px] opacity-60 hover:text-rose-400 inline-flex items-center gap-1"
                                    data-testid={`collection-delete-${c.id}`}
                                >
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
